/**
 * Admin Layout Component
 * Provides consistent layout for all admin pages
 * Completely isolated from CRM layout
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { Button } from '../../../components/ui/button';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../../components/ui/tooltip';
import CommandPalette, { CommandPaletteTrigger, pushRecentPage } from '../../../components/admin/CommandPalette';
import { Shield, LogOut, ChevronDown, Settings, Bell, Menu, PanelLeftClose, PanelLeftOpen, Star, Clock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../../../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetTrigger } from '../../../components/ui/sheet';
import { cn } from '../../../lib/utils';
import { NAVIGATION_ITEMS, ALL_NAV_ITEMS } from '../navigation';
import RouteErrorBoundary from './RouteErrorBoundary';

const RECENTS_KEY = 'cluvik-admin-recent-pages';
const FAVORITES_KEY = 'cluvik-admin-favorite-pages';
const COLLAPSED_KEY = 'cluvik-admin-sidebar-collapsed';

// NAVIGATION_ITEMS / ALL_NAV_ITEMS now live in ../navigation.js, shared with
// the command palette and the route guard so the three cannot disagree.
const ALL_ITEMS = ALL_NAV_ITEMS;

/**
 * Navigation the signed-in operator may actually use.
 *
 * Filtered from the backend's answer, so a Support operator never sees Plans &
 * Billing and a Read-Only operator never sees a control the API would refuse.
 * While capabilities are unknown this returns nothing rather than everything —
 * a blank sidebar for a moment is correct; an Owner-shaped sidebar for a moment
 * is not.
 */
const visibleNavigation = (can, ready) => {
  if (!ready) return [];
  return NAVIGATION_ITEMS
    .map((section) => ({ ...section, items: section.items.filter((i) => !i.permission || can(i.permission)) }))
    .filter((section) => section.items.length > 0);
};

const readStorageArray = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]');
  } catch {
    return [];
  }
};

const AdminLayout = () => {
  const { adminUser, adminLogout, can, capabilitiesLoaded, capabilityError } = useAdminAuth();
  const navigation = React.useMemo(
    () => visibleNavigation(can, capabilitiesLoaded),
    [can, capabilitiesLoaded],
  );
  // Recents and favourites must not resurrect a screen the operator lost access
  // to, so they resolve against the permitted set rather than the full list.
  const allowedItems = React.useMemo(() => navigation.flatMap((s) => s.items), [navigation]);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [favorites, setFavorites] = useState(() => readStorageArray(FAVORITES_KEY));
  const [recents, setRecents] = useState(() => readStorageArray(RECENTS_KEY));

  const handleLogout = () => {
    adminLogout();
    navigate('/login');
  };

  const isActivePath = (path) => (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));

  // Track recently visited pages for the sidebar "Recent" section + the
  // command palette. Pure client-side convenience, no backend involved.
  useEffect(() => {
    const match = ALL_ITEMS.find((i) => isActivePath(i.path));
    if (match) {
      pushRecentPage({ label: match.name, path: match.path });
      setRecents(readStorageArray(RECENTS_KEY));
    }
    setMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, String(next));
      return next;
    });
  };

  const toggleFavorite = (path, e) => {
    e.stopPropagation();
    setFavorites((prev) => {
      const next = prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path];
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
      return next;
    });
  };

  const favoriteItems = useMemo(
    () => allowedItems.filter((i) => favorites.includes(i.path)),
    [favorites, allowedItems]
  );

  const initials = adminUser?.first_name?.[0] || 'A';
  const operatorName = [adminUser?.first_name, adminUser?.last_name].filter(Boolean).join(' ') || 'Admin';

  // RENDER FUNCTIONS, NOT COMPONENTS (T29 / NX-23, S18). These two were
  // `const NavButton = () => ...` / `const SidebarBody = () => ...` declared in
  // this render, so every render handed React a NEW component type and it
  // unmounted and remounted every nav row. Favouriting one row, or anything
  // else that re-rendered the layout, dropped a keyboard user's focus onto
  // <body>. Called as plain functions they return elements of stable types,
  // and React keeps the DOM nodes (and focus) across renders.
  const renderNavButton = (item, { key, showFavoriteToggle = true, compact = false } = {}) => {
    const Icon = item.icon;
    const active = isActivePath(item.path);
    const isFav = favorites.includes(item.path);
    const slug = item.name.toLowerCase().replace(/\s/g, '-');
    const withToggle = showFavoriteToggle && !compact;

    const button = (
      <button
        type="button"
        onClick={() => navigate(item.path)}
        aria-current={active ? 'page' : undefined}
        // Collapsed to an icon the visible label is gone; the name must not go with it.
        aria-label={compact ? item.name : undefined}
        className={cn(
          'relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          active
            ? 'bg-primary/10 font-medium text-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          compact && 'justify-center px-2',
          withToggle && 'pr-10'
        )}
        data-testid={`admin-nav-${slug}`}
      >
        {active && !compact && (
          <span aria-hidden="true" className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-primary" />
        )}
        {/* `text-muted-foreground/70` was 2.89:1 in light — under the 3:1
            SC 1.4.11 floor for a graphic, and this glyph is the only thing
            distinguishing one nav row from the next when the sidebar is
            collapsed to icons. Full token; the ACTIVE state is still carried
            by `text-primary` and by the rail beside it. */}
        <Icon aria-hidden="true" className={cn('h-[18px] w-[18px] shrink-0', active ? 'text-primary' : 'text-muted-foreground')} />
        {!compact && (
          <div className="min-w-0 flex-1">
            <span className="block truncate">{item.name}</span>
          </div>
        )}
      </button>
    );

    if (compact) {
      return (
        <Tooltip key={key} delayDuration={200}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="right">{item.name}</TooltipContent>
        </Tooltip>
      );
    }
    if (!withToggle) return React.cloneElement(button, { key });

    // The favourite star is a SIBLING of the nav button, not a child. It used
    // to be a `role="button"` span with tabIndex -1 inside the <button>:
    // nested interactive content (invalid), unreachable by keyboard, unnamed,
    // folded into the nav row's own accessible name, 16px square, and visible
    // only on mouse hover — so on a touch screen it was an invisible target.
    return (
      <div key={key} className="group/nav relative">
        {button}
        <button
          type="button"
          onClick={(e) => toggleFavorite(item.path, e)}
          aria-pressed={isFav}
          aria-label={`Favorite ${item.name}`}
          title={isFav ? `Remove ${item.name} from favorites` : `Add ${item.name} to favorites`}
          data-testid={`admin-nav-favorite-${slug}`}
          className={cn(
            'absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md opacity-0 transition-opacity hover:bg-background focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/nav:opacity-100 [@media(hover:none)]:opacity-100',
            isFav && 'opacity-100'
          )}
        >
          <Star aria-hidden="true" className={cn('h-3.5 w-3.5', isFav ? 'fill-warning text-warning-text' : 'text-muted-foreground')} />
        </button>
      </div>
    );
  };

  // `compact` is the icon rail. Only the desktop sidebar honours the stored
  // preference: below lg the toggle is hidden, so a rail carried into the phone
  // drawer (or a 1440 window at 200% zoom) could not be widened again there.
  const renderSidebarBody = ({ compact = false } = {}) => (
    <div className="flex h-full flex-col">
      {!compact && (
        <div className="p-3">
          <CommandPaletteTrigger className="flex w-full items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted" />
        </div>
      )}

      <nav aria-label="Admin sections" className="flex-1 overflow-y-auto px-2.5 pb-4">
        {!compact && favoriteItems.length > 0 && (
          <div className="mb-5">
            <h3 className="mb-1.5 flex items-center gap-1.5 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <Star className="h-3 w-3" /> Favorites
            </h3>
            <div className="space-y-0.5">
              {favoriteItems.map((item) => renderNavButton(item, { key: `fav-${item.path}`, showFavoriteToggle: false, compact }))}
            </div>
          </div>
        )}

        {!compact && recents.length > 0 && (
          <div className="mb-5">
            <h3 className="mb-1.5 flex items-center gap-1.5 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <Clock className="h-3 w-3" /> Recent
            </h3>
            <div className="space-y-0.5">
              {recents.slice(0, 3).map((r) => {
                const item = allowedItems.find((i) => i.path === r.path);
                if (!item) return null;
                return renderNavButton(item, { key: `rec-${item.path}`, showFavoriteToggle: false, compact });
              })}
            </div>
          </div>
        )}

        {capabilityError && (
          // Permissions could not be loaded, so the sidebar is empty by design.
          // Say why, rather than leaving the operator staring at a blank panel
          // wondering whether their access was revoked.
          <div className="mx-3 mb-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
            Permissions could not be loaded, so no actions are shown. Reload to retry.
          </div>
        )}
        {navigation.map((section) => (
          <div key={section.section} className="mb-5">
            {!compact && (
              <h3 className="mb-1.5 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {section.section}
              </h3>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => renderNavButton(item, { key: item.path, compact }))}
            </div>
          </div>
        ))}
      </nav>

      <div className={cn('border-t border-border p-3', compact && 'flex justify-center')}>
        {/* Collapsed, this was a bare icon with no name. The name says what
            pressing it will do; aria-expanded says what state the sidebar is in. */}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          aria-controls="admin-sidebar"
          data-testid="admin-sidebar-collapse"
          className="hidden items-center gap-2 rounded-lg px-2.5 py-2 text-xs text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:flex"
        >
          {collapsed ? <PanelLeftOpen aria-hidden="true" className="h-4 w-4" /> : (
            <>
              <PanelLeftClose aria-hidden="true" className="h-4 w-4" /> Collapse
            </>
          )}
        </button>
        {/* 11px at 60% of a token with 0.18 of headroom: 2.42:1 in light. The
            smallest text in the application was also the dimmest. Uppercase,
            tracking and the 11px size already separate this tier from body
            text; the opacity was doing nothing the type was not. */}
        {!compact && <p className="mt-1 px-2.5 text-[11px] text-muted-foreground">Cluvik Control Plane v2.0</p>}
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      {/* The Sheet root wraps the layout (it renders no DOM) so the header's
          menu button can be its real Trigger: Radix then returns focus to it
          when the drawer closes. Opened by a bare onClick, Escape left a
          keyboard user's focus on <body> (S18 browser evidence). */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
      <div className="min-h-screen bg-background bg-grid-glow">
        {/* First focusable thing on every Admin page: past the header and the
            sidebar (a dozen-plus stops) straight to the page's own content. */}
        <a
          href="#admin-main"
          className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[60] focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Skip to main content
        </a>
        {/* Header */}
        <header className="fixed left-0 right-0 top-0 z-50 h-14 border-b border-border surface-glass">
          <div className="flex h-full items-center justify-between px-4">
            <div className="flex items-center gap-3">
              {/* The only way to reach navigation below lg. It was an unnamed icon. */}
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Open navigation"
                  data-testid="admin-mobile-nav-open"
                  className="lg:hidden"
                >
                  <Menu aria-hidden="true" className="h-5 w-5" />
                </Button>
              </SheetTrigger>

              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-chart-2 shadow-sm">
                  <Shield className="h-4 w-4 text-primary-foreground" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-sm font-semibold leading-tight text-foreground">Cluvik Control Plane</h1>
                  <p className="text-[11px] leading-tight text-muted-foreground">Platform Administration</p>
                </div>
              </div>
            </div>

            <div className="hidden max-w-md flex-1 px-8 md:block">
              <CommandPaletteTrigger className="mx-auto flex w-full max-w-sm items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted" />
            </div>

            <div className="flex items-center gap-1.5">
              {/* There is no notifications feed behind this button: it has never
                  had a handler. Not removed (that is a product call, NX-S18-03);
                  named and marked unavailable so assistive tech does not offer a
                  control that silently does nothing. */}
              <Button
                variant="ghost"
                size="icon"
                aria-label="Notifications (not available yet)"
                aria-disabled="true"
                title="Notifications are not available yet"
                className="cursor-not-allowed text-muted-foreground"
              >
                <Bell aria-hidden="true" className="h-[18px] w-[18px]" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  {/* Below sm the visible name is display:none, which left the
                      initial as the button's whole accessible name. */}
                  <Button variant="ghost" size="sm" aria-label={`Account menu for ${operatorName}`} className="flex items-center gap-2 pl-1.5">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                    <span className="hidden text-foreground sm:inline">{adminUser?.first_name || 'Admin'}</span>
                    <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-foreground">
                      {adminUser?.first_name} {adminUser?.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground">{adminUser?.email}</p>
                    <p className="mt-1 text-xs font-medium text-primary">Platform Admin</p>
                  </div>
                  <DropdownMenuSeparator />
                  {/* No settings screen exists (see navigation.js: "THERE IS NO
                      SETTINGS SECTION"). Announced as unavailable, not removed. */}
                  <DropdownMenuItem disabled>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings (not available yet)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive-text focus:text-destructive-text">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Desktop sidebar */}
        <aside
          id="admin-sidebar"
          aria-label="Admin sidebar"
          className={cn(
            'fixed bottom-0 left-0 top-14 z-40 hidden border-r border-border bg-card/60 backdrop-blur-sm transition-all duration-200 lg:block',
            collapsed ? 'w-[68px]' : 'w-64'
          )}
        >
          {renderSidebarBody({ compact: collapsed })}
        </aside>

        {/* Mobile sidebar */}
          <SheetContent side="left" className="w-72 p-0 pt-14">
            {/* A dialog with no title has no accessible name: a screen reader
                announced an unnamed dialog. */}
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">Admin Portal sections you can open.</SheetDescription>
            {renderSidebarBody()}
          </SheetContent>

        {/* Main content */}
        <main
          id="admin-main"
          tabIndex={-1}
          className={cn('pt-14 transition-all duration-200 focus:outline-none', collapsed ? 'lg:pl-[68px]' : 'lg:pl-64')}
        >
          <div className="min-h-[calc(100vh-56px)]">
            {/* One screen's render error no longer blanks the whole control
                plane: the shell stays, the screen says so inline. */}
            <RouteErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>

        <CommandPalette />
      </div>
      </Sheet>
    </TooltipProvider>
  );
};

export default AdminLayout;
